import { DialogRef } from '@angular/cdk/dialog';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatMenuModule } from '@angular/material/menu';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { NgDialogModule, onRouteChange$ } from '@principle-theorem/ng-shared';
import {
  BrandLinks,
  MarketingCampaignEntry,
  Patient,
} from '@principle-theorem/principle-core';
import {
  IMarketingCampaign,
  IMarketingCampaignEntry,
  IPatient,
  MarketingCampaignEntryStatus,
} from '@principle-theorem/principle-core/interfaces';
import { Firestore, multiFilter, WithRef } from '@principle-theorem/shared';
import { Observable } from 'rxjs';
import { EmptyStateComponent } from '../../empty-state/empty-state.component';
import { InformationBoxComponent } from '../../information-box/information-box.component';
import { PatientMarketingCampaignComponent } from './patient-marketing-campaign/patient-marketing-campaign.component';

export interface IPatientMarketingCampaignsDialogRequest {
  patient: WithRef<IPatient>;
}

@Component({
  selector: 'pr-patient-marketing-campaigns-dialog',
  imports: [
    NgDialogModule,
    InformationBoxComponent,
    MatTabsModule,
    MatMenuModule,
    MatTooltipModule,
    MatDividerModule,
    PatientMarketingCampaignComponent,
    EmptyStateComponent,
  ],
  templateUrl: './patient-marketing-campaigns-dialog.component.html',
  styleUrl: './patient-marketing-campaigns-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PatientMarketingCampaignsDialogComponent {
  private _router = inject(Router);
  private _dialogRef = inject(DialogRef);
  data = inject<IPatientMarketingCampaignsDialogRequest>(MAT_DIALOG_DATA);
  brandLinks = BrandLinks;
  pastEntries$: Observable<WithRef<IMarketingCampaignEntry>[]>;
  activeEntries$: Observable<WithRef<IMarketingCampaignEntry>[]>;

  constructor() {
    this.activeEntries$ = Patient.getCampaignEntries$(
      this.data.patient.ref
    ).pipe(multiFilter((entry) => MarketingCampaignEntry.isActive(entry)));
    this.pastEntries$ = Patient.getCampaignEntries$(this.data.patient.ref).pipe(
      multiFilter((entry) => !MarketingCampaignEntry.isActive(entry))
    );
    onRouteChange$(this._router)
      .pipe(takeUntilDestroyed())
      .subscribe(() => this._dialogRef.close());
  }

  getCampaign$(
    entry: WithRef<IMarketingCampaignEntry>
  ): Observable<WithRef<IMarketingCampaign>> {
    return Firestore.doc$(entry.campaignRef);
  }

  hasActions(entry: WithRef<IMarketingCampaignEntry>): boolean {
    return (
      entry.status !== MarketingCampaignEntryStatus.Preview &&
      !MarketingCampaignEntry.isFinalised(entry)
    );
  }
}
